import React, { Fragment, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import PageModal from '../../common/PageModal'
import SimpleButton from '../../common/SimpleButton'
import ToggleSwitch from '../../common/ToggleSwitch'
import CheckboxInput from '../../common/CheckboxInput'
import RadioInput from '../../common/RadioInput'
import ExpandableSection from '../../common/ExpandableSection'
import ConfirmationModal from '../../common/ConfirmationModal'
import SimpleTextInput from '../../common/SimpleTextInput'
import { toast } from '../../common/Toast'
import TooltipedIcon from '../../common/TooltipedIcon'
import { removeDocumentExternalFile, removeDocumentFile, updateDocumentExternalFile, updateDocumentFile } from '../../../actions/documents'
import { documentUploadDeletePermission, documentUploadWritePermission } from '../../../utils/permissionValidation'
import { capitalizeFirstLetter, capitalizeWords, getFileIcon } from '../../../utils/functions'
import { languagesShort } from '../../../utils/languages'
import { EditIcon, TrashCanFull, UrlIcon, CloseCircleIcon, CalendarIcon, InformationIcon } from '../../../icons'
import { checkPermission } from '../../../utils/permissionValidation'
import countries from '../../../utils/countries.json'
import { useWindowSize } from '../../../libs/hooks'
import { formatDateDayMonthYear } from '../../../utils/filters/date'
import '../uploads.scss'

const ViewDocumentModal = (props) => {
  const { t } = useTranslation()
  const isMobile = useWindowSize()[0] <= 768
  const { selectedDocument, handleEditDocument, closeModal } = props
  const [editedFile, setEditedFile] = useState(null)
  const [editedFileRegions, setEditedFileRegions] = useState([])
  const [hasMultiLanguage, setHasMultiLanguage] = useState(false)
  const [fileIdToDelete, setFileIdToDelete] = useState(null)
  const [showDeleteFileConfirmModal, setShowDeleteFileConfirmModal] = useState(false)
  const [showDiscardChangesConfirmModal, setShowDiscardChangesConfirmModal] = useState(false)
  const [expandedItem, setExpandedItem] = useState(null)
  const [haveError, setHaveError] = useState({})
  const documentStatuses = ['inactive', 'active']
  const hasEditPermission = checkPermission(props.userPermissions, documentUploadWritePermission)
  const hasDeletePermission = checkPermission(props.userPermissions, documentUploadDeletePermission)
  const tsbCategory = 'Technical Service Bulletins'

  useEffect(() => {
    if (editedFile) {
      setHasMultiLanguage(editedFile.multiLanguageCodes?.length > 0)
      setEditedFileRegions(!editedFile.isExternalDocument ? JSON.parse(JSON.stringify(editedFile.regions || [])) : [])
      setHaveError({})
      return
    }

    setEditedFileRegions([])
  }, [editedFile])

  const documentFooterContent = () => {
    return (
      <>
        <SimpleButton className="cancel-button" onClick={closeModal}>
          {t('close')}
        </SimpleButton>
        <SimpleButton className="action-button" onClick={handleEditDocument} requiredPermission={documentUploadWritePermission}>
          {t('edit')}
        </SimpleButton>
      </>
    )
  }

  const CustomDateInput = React.forwardRef(({ value, onClick }, ref) => (
    <div className={'custom-date-input' + (haveError[`publishDate`] ? ' has-error' : '')} ref={ref} onClick={onClick}>
      <div className={'date-text' + (!value ? ' placeholder-text' : '')}>{value ? value : ''}</div>
      {value && (
        <button
          className="reset-date-button"
          onClick={(e) => {
            e.stopPropagation()
            setEditedFile((prev) => ({ ...prev, regions: editedFileRegions, publishDate: null }))
          }}
        >
          <CloseCircleIcon />
        </button>
      )}
      <CalendarIcon className="calendar-icon" />
    </div>
  ))

  const renderViewFile = (fileIndex, fileName, id, revision, documentNumber, publishDate, status, regions, languageCode, multiLanguageCodes) => {
    return (
      <>
        {fileIndex !== 0 && <div className="inner-separator" />}
        <div className="row-wrapper">
          <div className="input-wrapper">
            <p className="input-option">
              {t('file_name')}
              {isMobile && (
                <div className="d-flex flex-row with-auto-margin-left align-center">
                  <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('document_context.toggle_text')}`} />
                  <div className="d-flex flex-row align-center with-margin-right" onClick={() => handleFileStatusUpdate(id)}>
                    <ToggleSwitch checked={status} />
                    <div className="without-font-weight">{t(documentStatuses[Number(status)])}</div>
                  </div>
                  {hasEditPermission && <EditIcon className="action action-icon" onClick={() => handleEditFile(id)} />}
                  {hasDeletePermission && selectedDocument.files.length > 1 && (
                    <TrashCanFull
                      className={`action-icon ${!hasEditPermission ? 'action' : ''} ${status ? 'disabled-action' : ''}`}
                      onClick={() => showDeleteConfirmationModal(id)}
                    />
                  )}
                </div>
              )}
            </p>
            <div className="value">
              {getFileIcon(fileName)}
              {fileName}
            </div>
          </div>
          {!isMobile && (
            <div className="input-wrapper max-content-width flex-row align-center">
              <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('document_context.toggle_text')}`} />
              <div className="d-flex flex-row with-auto-margin-left with-margin-right" onClick={() => handleFileStatusUpdate(id)}>
                <ToggleSwitch checked={status} />
                <div className="value">{t(documentStatuses[Number(status)])}</div>
              </div>
              {hasEditPermission && <EditIcon className="action action-icon" onClick={() => handleEditFile(id)} />}
              {hasDeletePermission && selectedDocument.files.length > 1 && (
                <TrashCanFull
                  className={`action-icon ${!hasEditPermission ? 'action' : ''} ${status ? 'disabled-action' : ''}`}
                  onClick={() => showDeleteConfirmationModal(id)}
                />
              )}
            </div>
          )}
        </div>
        <div className="row-wrapper">
          <div className="input-wrapper">
            <p className="input-option">{t('file_title')}</p>
            <div className="value">{`${selectedDocument.title.trim()}${revision?.length > 0 ? ` R${revision}` : ''} (${languageCode !== 'ML' ? languageCode : multiLanguageCodes?.join('/')})`}</div>
          </div>
        </div>
        <div className="row-wrapper">
          <div className="input-wrapper with-min-height">
            <p className="input-option">{t('document_context.document_number')}</p>
            <div className="value">{documentNumber}</div>
          </div>
          <div className="input-wrapper with-min-height">
            <p className="input-option">{t('document_context.revision_number')}</p>
            <div className="value">{revision}</div>
          </div>
          <div className="input-wrapper with-min-height">
            <p className="input-option">{t('publish_date')}</p>
            <div className="value">{publishDate ? formatDateDayMonthYear(publishDate) : ''}</div>
          </div>
        </div>
        <div className="row-wrapper">
          <div className="input-wrapper">
            <p className="input-option">{t('regions')}</p>
            <div className="ordered-list-wrapper">
              <div className="columns-wrapper">
                {regions?.map((region, index) => (
                  <div className="value" key={index}>
                    {region.name}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        {false &&
          regions?.map((region, index) => (
            <div className="row-wrapper" key={`countries-${index}`}>
              <ExpandableSection
                title={`${region.name} ${t('countries')}`}
                isExpanded={expandedItem === `${id}-${index}`}
                onToggle={() => toggleExpand(id, index)}
                className="internal-expandable"
              >
                <div className="ordered-list-wrapper">
                  <div className="columns-wrapper">
                    {region.countries?.includes('ALL') ? (
                      <div className="value">{t('all')}</div>
                    ) : (
                      region.countries?.map((country, index) => (
                        <div className="value is-read-only" key={index}>
                          {countries.find((c) => c.code === country)?.name}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </ExpandableSection>
            </div>
          ))}
        <div className="row-wrapper">
          <div className="input-wrapper">
            <p className="input-option">{t('languages')}</p>
            <div className="ordered-list-wrapper">
              <div className="columns-wrapper">
                {multiLanguageCodes?.length > 0 ? (
                  multiLanguageCodes.map((languageCode, index) => (
                    <div className="value" key={index}>
                      {`${t(languagesShort[languageCode.toLowerCase()] || languageCode)} (${languageCode})`}
                    </div>
                  ))
                ) : (
                  <div className="value">{`${t(languagesShort[languageCode?.toLowerCase()] || languageCode)} (${languageCode})`}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  const renderViewExternalFile = (fileIndex, url, id, status, languageCode, multiLanguageCodes) => {
    return (
      <>
        {fileIndex !== 0 && <div className="inner-separator" />}
        <div className="row-wrapper">
          <div className="input-wrapper">
            <p className="input-option">
              {t('url')}
              <div className="d-flex flex-align-center external-doc">
                <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('document_context.toggle_text')}`} />
                <div className="value without-width with-auto-margin-left" onClick={() => handleExternalFileStatusUpdate(id)}>
                  <ToggleSwitch checked={status} />
                  <div className="without-font-weight">{t(documentStatuses[Number(status)])}</div>
                </div>
                {hasEditPermission && <EditIcon className="action action-icon with-margin-left " onClick={() => handleEditFile(id)} />}
              </div>
            </p>
            <div className="value">{url}</div>
          </div>
        </div>
        <div className="row-wrapper">
          <div className="input-wrapper">
            <p className="input-option">{t('languages')}</p>
            <div className="ordered-list-wrapper">
              <div className="columns-wrapper">
                {multiLanguageCodes?.length > 0 ? (
                  multiLanguageCodes.map((languageCode, index) => (
                    <div className="value" key={index}>
                      {`${t(languagesShort[languageCode.toLowerCase()] || languageCode)} (${languageCode})`}
                    </div>
                  ))
                ) : (
                  <div className="value">{`${t(languagesShort[languageCode?.toLowerCase()] || languageCode)} (${languageCode})`}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  const renderEditFile = (fileIndex) => {
    return (
      <>
        {fileIndex !== 0 && <div className="inner-separator" />}
        <div className="row-wrapper">
          <div className="input-wrapper">
            <p className="input-option">
              {t('file_name')}
              {isMobile && (
                <div className="action d-flex flex-row without-font-weight align-center with-auto-margin-left">
                  <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('document_context.toggle_text')}`} />
                  <div className="d-flex flex-row align-center with-margin-right">
                    <ToggleSwitch
                      checked={editedFile.isActive}
                      onChange={() => setEditedFile((prev) => ({ ...prev, regions: editedFileRegions, isActive: !editedFile.isActive }))}
                    />
                    <div className="">{t(documentStatuses[Number(editedFile.isActive)])}</div>
                  </div>
                  <span className="action-label action" onClick={() => handleCancelEditFile()}>
                    {t('cancel')}
                  </span>
                  <span className="action-label submit-label" onClick={() => handleSaveFile()}>
                    {t('save')}
                  </span>
                </div>
              )}
            </p>
            <div className="value">
              {getFileIcon(editedFile.fileName)}
              {editedFile.fileName}
            </div>
          </div>
          {!isMobile && (
            <div className="input-wrapper max-content-width flex-row align-center">
              <div className="d-flex flex-row with-auto-margin-left align-center with-margin-right">
                <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('document_context.toggle_text')}`} />
                <ToggleSwitch
                  checked={editedFile.isActive}
                  onChange={() => setEditedFile((prev) => ({ ...prev, regions: editedFileRegions, isActive: !editedFile.isActive }))}
                />
                <div>{t(documentStatuses[Number(editedFile.isActive)])}</div>
              </div>
              <span className="action-label action" onClick={() => handleCancelEditFile()}>
                {t('cancel')}
              </span>
              <span className="action-label submit-label" onClick={() => handleSaveFile()}>
                {t('save')}
              </span>
            </div>
          )}
        </div>
        <div className="row-wrapper">
          <div className="input-wrapper">
            <p className="input-option">{t('file_title')}</p>
            <div className="value">{`${selectedDocument.title.trim()}${editedFile.revision?.length > 0 ? ` R${editedFile.revision}` : ''} (${editedFile.languageCode !== 'ML' ? editedFile.languageCode : editedFile.multiLanguageCodes?.join('/')})`}</div>
          </div>
        </div>
        <div className="row-wrapper">
          <div className="input-wrapper">
            <p className="input-option">{t('document_context.document_number')}</p>
            <input
              type="text"
              className={'name-input'}
              value={editedFile.documentNumber}
              onChange={(e) => setEditedFile((prev) => ({ ...prev, regions: editedFileRegions, documentNumber: e.target.value }))}
            />
          </div>
          <div className="input-wrapper">
            <p className="input-option">{t('document_context.revision_number')}</p>
            <input
              type="text"
              className={'name-input'}
              value={editedFile.revision}
              onChange={(e) => setEditedFile((prev) => ({ ...prev, regions: editedFileRegions, revision: e.target.value }))}
            />
          </div>
          <div className="input-wrapper">
            <p className="input-option">{`${t('publish_date')}${selectedDocument.documentCategory === tsbCategory ? '*' : ''}`}</p>
            <DatePicker
              selected={editedFile.publishDate ? new Date(editedFile.publishDate) : ''}
              onChange={(date) => {
                setEditedFile((prev) => ({
                  ...prev,
                  regions: editedFileRegions,
                  publishDate: new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())).toISOString(),
                }))
              }}
              dateFormat={'yyyy-MM-dd'}
              yearItemNumber={9}
              customInput={<CustomDateInput />}
              showYearDropdown
              showMonthDropdown
            />
          </div>
        </div>
        <div className="row-wrapper">
          <div className="input-wrapper">
            <p className="input-option">{t('regions')}</p>
            <div className="ordered-list-wrapper">
              <div className="columns-wrapper">
                {props.regions.map((region, index) => (
                  <div className="item-wrapper" key={index}>
                    <CheckboxInput
                      checked={editedFileRegions.map((r) => r.name).includes(region.name)}
                      text={region.name}
                      onChange={() => handleCheckRegion(region)}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        {false &&
          editedFileRegions.map((region, index) => (
            <div className="row-wrapper" key={`countries-${index}`}>
              <ExpandableSection
                title={`${region.name} ${t('countries')}`}
                isExpanded={expandedItem === `${editedFile.id}-${index}`}
                onToggle={() => toggleExpand(editedFile.id, index)}
                className="internal-expandable"
                additionalContent={
                  expandedItem === `${editedFile.id}-${index}` ? (
                    <div className="expandable-action">
                      <span
                        className={props.regions.find((r) => r.name === region.name)?.countries?.length !== region.countries?.length ? 'available' : ''}
                        onClick={(e) => {
                          e.stopPropagation()
                          setEditedFileRegions(
                            editedFileRegions.map((r) =>
                              r.name === region.name
                                ? {
                                    ...r,
                                    countries: props.regions.find((reg) => reg.name === region.name)?.countries || [],
                                  }
                                : r,
                            ),
                          )
                        }}
                      >
                        {t('select_all')}
                      </span>
                      <span>|</span>
                      <span
                        className={region.countries?.length > 0 ? 'available' : ''}
                        onClick={(e) => {
                          e.stopPropagation()
                          setEditedFileRegions(
                            editedFileRegions.map((r) =>
                              r.name === region.name
                                ? {
                                    ...r,
                                    countries: [],
                                  }
                                : r,
                            ),
                          )
                        }}
                      >
                        {capitalizeWords(t('clear_all'))}
                      </span>
                    </div>
                  ) : (
                    <div className="counter-badge">
                      {region.countries?.includes('ALL') ? props.regions.find((r) => r.name === region.name)?.countries?.length : region.countries?.length || 0}
                    </div>
                  )
                }
              >
                <div className="ordered-list-wrapper">
                  <div className="columns-wrapper">
                    {props.regions
                      .find((r) => r.name === region.name)
                      ?.countries?.map((country) => (
                        <div className="item-wrapper" key={country}>
                          <CheckboxInput
                            checked={region.countries?.includes(country) || region.countries?.includes('ALL')}
                            text={countries.find((c) => c.code === country)?.name || ''}
                            onChange={() => handleCheckCountry(region.name, country)}
                          />
                        </div>
                      ))}
                  </div>
                </div>
              </ExpandableSection>
            </div>
          ))}
        <div className="row-wrapper">
          <div className="input-wrapper">
            <p className="input-option">{t('languages')}</p>
            <CheckboxInput checked={hasMultiLanguage} text={t('multilanguage')} onChange={() => setHasMultiLanguage((prevValue) => !prevValue)} />
            <div className="ordered-list-wrapper">
              <div className="columns-wrapper">
                {props.documentLanguages.map((language, index) => (
                  <div className="item-wrapper" key={index}>
                    {hasMultiLanguage ? (
                      <CheckboxInput
                        checked={editedFile.multiLanguageCodes?.includes(language)}
                        text={`${t(languagesShort[language.toLowerCase()] || language)} (${language})`}
                        onChange={() => handleCheckMultiLanguage(language)}
                      />
                    ) : (
                      <RadioInput
                        checked={editedFile.languageCode === language}
                        text={`${t(languagesShort[language.toLowerCase()] || language)} (${language})`}
                        onChange={() => handleCheckSingleLanguage(language)}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  const renderEditExternalFile = (fileIndex) => {
    return (
      <>
        {fileIndex !== 0 && <div className="inner-separator" />}
        <div className="row-wrapper">
          <div className="input-wrapper">
            <p className="input-option">
              {t('url')}
              <div className="value without-width with-auto-margin-left">
                <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('document_context.toggle_text')}`} />
                <ToggleSwitch checked={editedFile.isActive} onChange={() => setEditedFile((prev) => ({ ...prev, isActive: !prev.isActive }))} />
                <div className="without-font-weight">{t(documentStatuses[Number(editedFile.isActive)])}</div>
              </div>
              <div className="d-flex flex-row with-margin-left without-font-weight">
                <span className="action-label" onClick={() => handleCancelEditFile()}>
                  {t('cancel')}
                </span>
                <span className="action-label submit-label" onClick={() => handleSaveFile()}>
                  {t('save')}
                </span>
              </div>
            </p>
            <div className="value">
              <div className="input-wrapper">
                <SimpleTextInput
                  icon={<UrlIcon />}
                  value={editedFile.url}
                  onChange={(url) => setEditedFile((prev) => ({ ...prev, url: url }))}
                  placeholder={t('document_context.please_insert_document_url')}
                  onClear={() => setEditedFile((prev) => ({ ...prev, url: '' }))}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="row-wrapper">
          <div className="input-wrapper">
            <p className="input-option">{t('languages')}</p>
            <CheckboxInput checked={hasMultiLanguage} text={t('multilanguage')} onChange={() => setHasMultiLanguage((prevValue) => !prevValue)} />
            <div className="ordered-list-wrapper">
              <div className="columns-wrapper">
                {props.documentLanguages.map((language, index) => (
                  <div className="item-wrapper" key={index}>
                    {hasMultiLanguage ? (
                      <CheckboxInput
                        checked={editedFile.multiLanguageCodes?.includes(language)}
                        text={`${t(languagesShort[language.toLowerCase()] || language)} (${language})`}
                        onChange={() => handleCheckMultiLanguage(language)}
                      />
                    ) : (
                      <RadioInput
                        checked={editedFile.languageCode === language}
                        text={`${t(languagesShort[language.toLowerCase()] || language)} (${language})`}
                        onChange={() => handleCheckSingleLanguage(language)}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  const handleCheckRegion = (region) => {
    if (editedFileRegions.map((r) => r.name).includes(region.name)) {
      setEditedFileRegions(editedFileRegions.filter((r) => r.name !== region.name))
      return
    }

    setEditedFileRegions([...editedFileRegions, JSON.parse(JSON.stringify(region))])
  }

  const handleCheckCountry = (regionName, country) => {
    const updatedRegions = editedFileRegions.map((r) => {
      if (r.name === regionName) {
        if (r.countries.includes('ALL')) {
          r.countries = props.regions.find((region) => region.name === regionName).countries.filter((c) => c !== country)
        } else if (r.countries.includes(country)) {
          r.countries = r.countries.filter((c) => c !== country)
        } else {
          if (r.countries.length === props.regions.find((region) => region.name === regionName).countries.length - 1) {
            r.countries = ['ALL']
          } else {
            r.countries = [...r.countries, country]
          }
        }
      }

      return r
    })

    setEditedFileRegions(updatedRegions)
  }

  const handleCheckMultiLanguage = (language) => {
    if (editedFile.multiLanguageCodes.includes(language)) {
      setEditedFile((prev) => ({
        ...prev,
        ...(!selectedDocument.isExternalDocument && { regions: editedFileRegions }),
        multiLanguageCodes: editedFile.multiLanguageCodes.filter((l) => l !== language),
      }))
      return
    }

    setEditedFile((prev) => ({
      ...prev,
      ...(!selectedDocument.isExternalDocument && { regions: editedFileRegions }),
      languageCode: 'ML',
      multiLanguageCodes: [...editedFile.multiLanguageCodes, language],
    }))
  }

  const handleCheckSingleLanguage = (language) => {
    setEditedFile((prev) => ({
      ...prev,
      ...(!selectedDocument.isExternalDocument && { regions: editedFileRegions }),
      languageCode: language,
      multiLanguageCodes: [],
    }))
  }

  const toggleExpand = (id, index) => {
    setExpandedItem(expandedItem === `${id}-${index}` ? null : `${id}-${index}`)
  }

  const showDeleteConfirmationModal = (id) => {
    setFileIdToDelete(id)
    setShowDeleteFileConfirmModal(true)
  }

  const handleDeleteFile = () => {
    setShowDeleteFileConfirmModal(false)

    selectedDocument.isExternalDocument
      ? props.actions.removeDocumentExternalFile(selectedDocument.id, fileIdToDelete)
      : props.actions.removeDocumentFile(selectedDocument.id, fileIdToDelete)
  }

  const handleEditFile = (fileId) => {
    if (selectedDocument.isExternalDocument) {
      const file = selectedDocument.externalDocuments.find((file) => file.id === fileId)
      setEditedFile(file)

      return
    }

    const file = selectedDocument.files.find((file) => file.id === fileId)
    setExpandedItem(null)
    setEditedFile(file)
  }

  const hasFileChanges = () => {
    const fileWithoutChanges = selectedDocument.isExternalDocument
      ? selectedDocument.externalDocuments.find((file) => file.id === editedFile.id)
      : selectedDocument.files.find((file) => file.id === editedFile.id)

    if (JSON.stringify(fileWithoutChanges) === JSON.stringify({ ...editedFile, ...(!selectedDocument.isExternalDocument && { regions: editedFileRegions }) })) {
      setEditedFile(null)

      return false
    }

    return true
  }

  const handleCancelEditFile = () => {
    if (!hasFileChanges()) {
      return
    }

    setShowDiscardChangesConfirmModal(true)
  }

  const handleDiscardChanges = () => {
    setShowDiscardChangesConfirmModal(false)
    setEditedFile(null)
  }

  const isValidInput = () => {
    let errors = {}
    let isValid = true

    if (selectedDocument.documentCategory === tsbCategory) {
      if (!editedFile.publishDate) {
        errors = { ...errors, publishDate: true }
        isValid = false
      }
    }

    setHaveError(errors)
    return isValid
  }

  const handleSaveFile = () => {
    if (!hasFileChanges()) {
      return
    }

    if (!isValidInput()) {
      toast.error(t('error.fill_all_fields'))
      return
    }

    selectedDocument.isExternalDocument
      ? props.actions.updateDocumentExternalFile(selectedDocument.id, editedFile.id, editedFile).then(() => setEditedFile(null))
      : props.actions
          .updateDocumentFile(selectedDocument.id, editedFile.id, {
            ...editedFile,
            revision: editedFile.revision || undefined,
            documentNumber: editedFile.documentNumber || undefined,
            regions: editedFileRegions.map((r) => ({
              id: props.regions.find((region) => region.name === r.name)?.id,
              name: r.name,
              countries: props.regions.find((region) => region.name === r.name)?.countries?.length === r.countries?.length ? ['ALL'] : r.countries,
            })),
          })
          .then(() => setEditedFile(null))
  }

  const handleFileStatusUpdate = (fileId) => {
    const updatedFile = selectedDocument.files.find((file) => file.id === fileId)

    if (updatedFile) {
      props.actions.updateDocumentFile(selectedDocument.id, updatedFile.id, {
        ...updatedFile,
        documentNumber: updatedFile.documentNumber || undefined,
        revision: updatedFile.revision || undefined,
        isActive: !updatedFile.isActive,
        regions: updatedFile.regions?.map((r) => ({
          id: props.regions.find((region) => region.name === r.name)?.id,
          name: r.name,
          countries: props.regions.find((region) => region.name === r.name)?.countries?.length === r.countries?.length ? ['ALL'] : r.countries,
        })),
      })
    }
  }

  const handleExternalFileStatusUpdate = (fileId) => {
    const updatedFile = selectedDocument.externalDocuments.find((file) => file.id === fileId)

    if (updatedFile) {
      props.actions.updateDocumentExternalFile(selectedDocument.id, updatedFile.id, { ...updatedFile, isActive: !updatedFile.isActive })
    }
  }

  return (
    <>
      <PageModal toggle onToggle={closeModal} className="uploads-modal" title={t('details')} footerContent={documentFooterContent()}>
        <div className="uploads-modal-content">
          <div className="section-title">{t('general_information').toUpperCase()}</div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('title')}</p>
              <div className="value">{selectedDocument.title}</div>
            </div>
          </div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('access_level')}</p>
              <div className="value">{capitalizeFirstLetter(selectedDocument.accessLevel)}</div>
            </div>
            <div className="input-wrapper">
              <p className="input-option">{t('document_context.document_type')}</p>
              <div className="value">{selectedDocument.documentType}</div>
            </div>
            <div className="input-wrapper">
              <p className="input-option">{t('category')}</p>
              <div className="value">{selectedDocument.documentCategory}</div>
            </div>
          </div>
          <div className="separator" />
          <div className="section-title">
            {selectedDocument.isExternalDocument ? t('document_context.external_documents').toUpperCase() : t('nav.documents').toUpperCase()}
          </div>
          {selectedDocument?.externalDocuments?.map((externalDocument, index) => (
            <Fragment key={index}>
              {editedFile?.id === externalDocument.id
                ? renderEditExternalFile(index)
                : renderViewExternalFile(
                    index,
                    externalDocument.url,
                    externalDocument.id,
                    externalDocument.isActive,
                    externalDocument.languageCode,
                    externalDocument.multiLanguageCodes,
                  )}
            </Fragment>
          ))}
          {selectedDocument?.files?.map((file, index) => (
            <Fragment key={index}>
              {editedFile?.id === file.id
                ? renderEditFile(index)
                : renderViewFile(
                    index,
                    file.fileName,
                    file.id,
                    file.revision,
                    file.documentNumber,
                    file.publishDate,
                    file.isActive,
                    file.regions,
                    file.languageCode,
                    file.multiLanguageCodes,
                  )}
            </Fragment>
          ))}
          <div className="separator" />
          <div className="row-wrapper">
            <div className="input-wrapper">
              <p className="input-option">{t('monitoring.models')}</p>
              <div className="ordered-list-wrapper">
                <div className="columns-wrapper">
                  {selectedDocument.models?.map((model, index) => (
                    <div className="value" key={index}>
                      {model}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </PageModal>
      {showDiscardChangesConfirmModal && (
        <ConfirmationModal
          onToggle={() => setShowDiscardChangesConfirmModal(false)}
          onAccept={() => handleDiscardChanges()}
          onCancel={() => setShowDiscardChangesConfirmModal(false)}
          message={t('discard_confirmation')}
        />
      )}
      {showDeleteFileConfirmModal && (
        <ConfirmationModal
          onToggle={() => setShowDeleteFileConfirmModal(false)}
          onAccept={() => handleDeleteFile()}
          onCancel={() => setShowDeleteFileConfirmModal(false)}
          message={t('confirm_delete_file')}
          acceptButtonText={t('delete')}
          isWarning
          requiredPermission={documentUploadDeletePermission}
        />
      )}
    </>
  )
}

function stateToProps({ userPermissions, shared }) {
  return {
    userPermissions,
    documentLanguages: shared?.languages?.map((l) => l.languageCode) || [],
    regions: shared?.regions || [],
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        removeDocumentFile,
        removeDocumentExternalFile,
        updateDocumentFile,
        updateDocumentExternalFile,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(ViewDocumentModal)

